import { css, jsx } from '@emotion/react';
import { Colors } from '@zapier/design-system';
import { useRef, useState, useCallback, Fragment } from 'react';
import { IconButton } from '../IconButton/IconButton.es.js';
import { BREAKPOINTS } from '../constants.es.js';

const Styles = {
  carouselContainer: css`
    position: relative;
  `,
  scrollableContainer: (gutterWidth) => css`
    width: 100%;

    // Allow content drop-shadow to be visible if it exists
    padding-bottom: var(--zds-space-12, 12px);
    margin-bottom: calc(var(--zds-space-12, 12px) * -1);

    overflow-x: scroll;
    overflow-y: hidden;

    // Hide scrollbar on all browsers
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none;
    }

    // On mobile, we want to go into the gutter to make the scrollable area full width
    @media screen and (max-width: calc(${BREAKPOINTS.tablet} - 1px)) {
      margin-left: -${gutterWidth}px;
      padding-left: ${gutterWidth}px;

      margin-right: -${gutterWidth}px;
      padding-right: ${gutterWidth}px;
    }
  `,
  carouselStyle: (gutterWidth, addSpaceForRightArrow) => [
    css`
      display: flex;
      flex-direction: row;
      gap: var(--zds-space-20, 20px);
      width: 100%;

      // On mobile, add a spacer after the last child to account for gutter
      @media screen and (max-width: calc(${BREAKPOINTS.tablet} - 1px)) {
        > *:last-child {
          position: relative;

          &:after {
            content: '';
            display: block;
            position: absolute;
            right: -${gutterWidth}px;
            width: ${gutterWidth}px;
            height: 1px;
          }
        }
      }
    `,
    addSpaceForRightArrow && css`
        // On tablet/desktop, add a spacer after the last card to account for arrow click area
        @media screen and (min-width: ${BREAKPOINTS.tablet}) {
          > *:last-child {
            position: relative;

            &:after {
              content: '';
              display: block;
              position: absolute;
              right: calc(var(--zds-space-48, 48px) * -1);
              width: var(--zds-space-48, 48px);
              height: 1px;
            }
          }
        }
      `
  ],
  arrowButton: css`
    display: none;

    @media screen and (min-width: ${BREAKPOINTS.tablet}) {
      display: flex;
      position: absolute;
      top: 0;
      height: 100%;
      width: var(--zds-space-48, 50px);
      border: none;
      z-index: 3;
      align-items: center;
      justify-content: center;
    }
  `,
  leftArrow: css`
    left: 0;
  `,
  rightArrow: css`
    right: 0;
  `,
  leftGradient: (backgroundColor, gutterWidth) => css`
    position: absolute;
    height: 100%;
    width: var(--zds-space-48, 60px);
    left: -${gutterWidth}px;
    z-index: 2;
    background: linear-gradient(
      to left,
      transparent 0%,
      ${Colors[backgroundColor]} 85%
    );

    @media screen and (min-width: ${BREAKPOINTS.tablet}) {
      left: 0;
      width: calc(var(--zds-space-40, 40px) * 2);
    }
  `,
  rightGradient: (backgroundColor, gutterWidth) => css`
    position: absolute;
    height: 100%;
    width: var(--zds-space-48, 60px);
    right: -${gutterWidth}px;
    z-index: 2;
    background: linear-gradient(
      to right,
      transparent 0%,
      ${Colors[backgroundColor]} 85%
    );

    @media screen and (min-width: ${BREAKPOINTS.tablet}) {
      right: 0;
      width: calc(var(--zds-space-40, 40px) * 2);
    }
  `
};
const Carousel = ({ children, id, role, elementWidth = 300, arrowButtonColor = "tertiary", arrowButtonSize = "medium", pageBackgroundColor = "BackgroundWeaker", pageGutterWidth = 20, disableScrollButtons = false, onLeftArrowClick, onRightArrowClick, className }) => {
  var _a, _b;
  const scrollContainerRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollRightInitial, setCanScrollRightInitial] = useState(false);
  const carouselRef = useCallback((node) => {
    if (node !== null) {
      setCanScrollRightInitial(node.scrollWidth > node.getBoundingClientRect().width);
    }
  }, []);
  const handleLeftArrowClick = () => {
    var _a2;
    (_a2 = scrollContainerRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollBy({
      left: scrollContainerRef.current ? -Math.round(scrollContainerRef.current.clientWidth * 0.75) : -elementWidth,
      behavior: "smooth"
    });
    onLeftArrowClick === null || onLeftArrowClick === void 0 ? void 0 : onLeftArrowClick();
  };
  const handleRightArrowClick = () => {
    var _a2;
    (_a2 = scrollContainerRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollBy({
      left: scrollContainerRef.current ? Math.round(scrollContainerRef.current.clientWidth * 0.75) : elementWidth,
      behavior: "smooth"
    });
    onRightArrowClick === null || onRightArrowClick === void 0 ? void 0 : onRightArrowClick();
  };
  const scrollHandler = () => {
    var _a2, _b2;
    setScrollPosition((_b2 = (_a2 = scrollContainerRef.current) === null || _a2 === void 0 ? void 0 : _a2.scrollLeft) !== null && _b2 !== void 0 ? _b2 : 0);
  };
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollContainerRef.current ? scrollPosition + ((_a = scrollContainerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) < ((_b = scrollContainerRef.current) === null || _b === void 0 ? void 0 : _b.scrollWidth) : canScrollRightInitial;
  return jsx(
    "div",
    { className, css: Styles.carouselContainer, id, role },
    canScrollLeft && jsx(
      Fragment,
      null,
      jsx("div", { css: Styles.leftGradient(pageBackgroundColor, pageGutterWidth) }),
      jsx(
        "div",
        { css: [Styles.arrowButton, Styles.leftArrow] },
        jsx(IconButton, { name: "arrowSmallLeft", variant: arrowButtonColor, size: arrowButtonSize, "aria-label": "Scroll right through carousel", isDisabled: disableScrollButtons, onPress: handleLeftArrowClick, style: { borderRadius: "50%" } })
      )
    ),
    canScrollRight && jsx(
      Fragment,
      null,
      jsx("div", { css: Styles.rightGradient(pageBackgroundColor, pageGutterWidth) }),
      jsx(
        "div",
        { css: [Styles.arrowButton, Styles.rightArrow] },
        jsx(IconButton, { name: "arrowSmallRight", variant: arrowButtonColor, size: arrowButtonSize, "aria-label": "Scroll left through carousel", isDisabled: disableScrollButtons, onPress: handleRightArrowClick, style: { borderRadius: "50%" } })
      )
    ),
    jsx(
      "div",
      { css: Styles.scrollableContainer(pageGutterWidth), ref: scrollContainerRef, onScroll: scrollHandler },
      jsx("div", { ref: carouselRef, css: Styles.carouselStyle(pageGutterWidth, canScrollRightInitial) }, children)
    )
  );
};

export { Carousel };
//# sourceMappingURL=Carousel.es.js.map
